<template>
  <div
    :style="customWrapperStyle"
  >
    <template
      v-if="cellType === 'image-icon'"
    >
      <v-img
        :src="cellData"
        :style="getImageIconStyle(header)"
      />
    </template>

    <template
      v-else-if="cellType === 'image'"
    >
      <v-img
        :src="cellData"
        contain
        :style="getImageIconStyle(header)"
      />
    </template>

    <template
      v-else-if="cellType === 'icon'"
    >
      <v-icon
        :style="getIconStyle(header)"
      >
        {{ cellData }}
      </v-icon>
    </template>

    <template
      v-else-if="cellType === 'status'"
    >
      <!-- The framed label statuses used to be: text and frame in the status's text
           colour over its background. Kept for the screens that ask for it with
           `statusVariant: 'pill'` - see `getStatusPillStyle`. -->
      <div
        v-if="isStatusPill(header)"
        :style="getStatusPillStyle(header)"
      >
        {{ getStatusCellData(header) }}
      </div>
      <status-indicator
        v-else
        :label="getStatusCellData(header)"
        :color="getStatusColors(header).background"
        :border-color="getStatusColors(header).text"
        :align="getStatusAlign(header)"
      />
    </template>

    <template
      v-else-if="header && header.component"
    >
      <component
        :is="header.component"
        :cell-data="cellData"
        :data="cellData"
        :cell-config="cellsConfig"
        :global-config="globalConfig"
        :row-data="rowData"
        :row-index="rowIndex"
        :fetch-table-data="fetchTableData"
        :header-cell-config="headerCellConfig"
        :config="header"
      />
    </template>

    <template v-else>
      {{ cellData }}
    </template>
  </div>
</template>

<script>
import { isObject } from '@/global/utils/utils'
import StatusIndicator from './StatusIndicator.vue'

const DEFAULT_IMAGE_ICON_STYLE = {
  width: '180%',
  height: '100%'
}

const DEFAULT_ICON_STYLE = {
  width: '18px',
  height: '18px'
}

const DEFAULT_STATUS_ALIGN = 'left'
const STATUS_PILL_VARIANT = 'pill'
const DEFAULT_STATUS_PILL_COLOR = '#000000'
export default {
  name: 'TableCellContent',

  components: {
    StatusIndicator
  },

  props: {
    cellType: {
      type: null,
      required: true,
      default: 'base'
    },
    cellData: {
      type: [Object, String, Number, Boolean, Array],
      default: null
    },
    rowData: {
      type: Object,
      default: () => ({})
    },
    header: {
      type: Object,
      required: true
    },
    cellsConfig: {
      type: Object,
      required: true,
      default: () => ({})
    },
    headerCellConfig: {
      type: Object,
      default: () => ({})
    },
    globalConfig: {
      type: Object,
      default: () => ({})
    },
    fetchTableData: {
      type: Function,
      default: () => {}
    },
    rowIndex: {
      type: Number,
      default: null
    }
  },

  computed: {
    customWrapperStyle () {
      const { key } = this.header || {}
      const { wrapperStyle } = this.cellsConfig?.[key] || {}
      return wrapperStyle && isObject(wrapperStyle)
        ? wrapperStyle
        : {
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            height: 'inherit',
            alignContent: 'center',
            overflow: 'hidden'
          }
    }
  },

  methods: {
    getImageIconStyle (header) {
      const { key } = header || {}
      const customStyle = DEFAULT_IMAGE_ICON_STYLE

      if (!key) {
        return customStyle
      }

      const { imageStyle } = this.cellsConfig[key] || {}

      return {
        ...customStyle,
        ...imageStyle
      }
    },

    getIconStyle (header) {
      const { key } = header || {}
      const customStyle = DEFAULT_ICON_STYLE

      if (!key) {
        return customStyle
      }

      const { iconStyle } = this.cellsConfig[key] || {}

      return {
        ...customStyle,
        ...iconStyle
      }
    },

    /**
     * Statuses read left-aligned by default. A config can override that per
     * column with `statusAlign: 'center' | 'right'`.
     */
    getStatusAlign (header) {
      const { key } = header || {}
      const { statusAlign } = (key && this.cellsConfig[key]) || {}
      return statusAlign || DEFAULT_STATUS_ALIGN
    },

    /**
     * The status colours the config points at, handed to StatusIndicator as the
     * dot's fill (background) and outline (text). The label itself is no longer
     * coloured - it follows the table's own text colour like every other cell.
     */
    getStatusColors (header) {
      const { key } = header || {}

      if (!key) {
        return {}
      }

      const { textColorKey, backgroundColorKey, colorObjectKey } = this.cellsConfig[key] || {}
      const colors = (colorObjectKey && this.cellData?.[colorObjectKey]) || {}

      return {
        background: colors[backgroundColorKey] ?? null,
        text: colors[textColorKey] ?? null
      }
    },

    isStatusPill (header) {
      const { key } = header || {}
      const { statusVariant } = (key && this.cellsConfig[key]) || {}
      return statusVariant === STATUS_PILL_VARIANT
    },

    /**
     * The framed label: text and frame in the status's text colour, filled with its
     * background, laid out by the column's own `statusStyle`.
     */
    getStatusPillStyle (header) {
      const { key } = header || {}
      const { statusStyle } = (key && this.cellsConfig[key]) || {}
      const { background, text } = this.getStatusColors(header)

      return {
        ...(isObject(statusStyle) ? statusStyle : {}),
        color: text ?? DEFAULT_STATUS_PILL_COLOR,
        border: text ? '1px solid' : '',
        borderColor: text ?? DEFAULT_STATUS_PILL_COLOR,
        backgroundColor: background ?? 'transparent'
      }
    },

    getStatusCellData (header) {
      const { displayValue } = this.cellsConfig[header.key] || {}
      return this.cellData?.[displayValue] || '-'
    }
  }
}
</script>
